import { ClipboardList, Plus, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  COMMANDE_STATUTS,
  COMMANDE_STATUT_LABELS,
  type Client,
  type Commande,
  type CommandeStatut,
} from '@sirajoptic/shared';
import { BadgeAVerifier, BadgeReste, BadgeStatutCommande } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox, Input, Select } from '@/components/ui/field';
import { ChargementBloc, EtatErreur, EtatVide } from '@/components/ui/feedback';
import { Pagination } from '@/components/ui/pagination';
import { TBody, TD, TH, THTri, THead, TR, Table } from '@/components/ui/table';
import { PageBody, PageHeader } from '@/layouts/AppShell';
import { useAntiRebond } from '@/lib/anti-rebond';
import { date, dh, ordonnance, telephone, tronquer } from '@/lib/format';
import { useCommandes, useSocietes } from './api/commandes.api';
import { CommandeDrawer } from './components/CommandeDrawer';
import { CommandeForm } from './components/CommandeForm';

type Tri = 'date_commande' | 'client_nom' | 'total' | 'reste';

/**
 * Écran de travail principal. Les colonnes reprennent **l'ordre exact** de sa
 * feuille Excel pour que le passage de l'une à l'autre ne demande aucun effort :
 * Date, Nom, Tél, N° de Bon, OD, OG, ADD, Verre, Prix, Monture, Prix,
 * Lentilles, Prix, Total, Avance, Reste, Société.
 */
export function CommandesPage() {
  const [params, setParams] = useSearchParams();

  const [recherche, setRecherche] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [societe, setSociete] = useState('');
  const [statut, setStatut] = useState<CommandeStatut | ''>('');
  const [nonSoldees, setNonSoldees] = useState(params.get('non_soldees') === '1');
  const [aVerifier, setAVerifier] = useState(params.get('a_verifier') === '1');
  const [page, setPage] = useState(1);
  const [tri, setTri] = useState<Tri>('date_commande');
  const [sens, setSens] = useState<'asc' | 'desc'>('desc');

  const [detail, setDetail] = useState<string | null>(params.get('id'));
  const [formulaire, setFormulaire] = useState<{
    ouvert: boolean;
    commande: Commande | null;
    client?: { client: Client; derniereOrdonnance: Commande | null } | null;
  }>({ ouvert: params.get('nouvelle') === '1', commande: null });

  /* Le champ garde la valeur immédiate — la frappe reste fluide — mais la
   * requête attend la fin de la saisie. Sans cela, chaque lettre coûtait deux
   * balayages de la table : la liste et son comptage, soit ~21 000 lignes lues
   * par touche. Un nom de neuf lettres en coûtait 177 000. */
  const rechercheDifferee = useAntiRebond(recherche);

  const { data: societes } = useSocietes();

  const requete = useMemo(
    () => ({
      page,
      limit: 25,
      q: rechercheDifferee || undefined,
      from: from || undefined,
      to: to || undefined,
      societe: societe || undefined,
      statut: statut || undefined,
      non_soldees: nonSoldees || undefined,
      a_verifier: aVerifier || undefined,
      tri,
      sens,
    }),
    [page, rechercheDifferee, from, to, societe, statut, nonSoldees, aVerifier, tri, sens],
  );

  const { data, isPending, error, refetch, isFetching } = useCommandes(requete);

  // Les filtres profonds restent partageables par l'URL (liens du tableau de bord).
  useEffect(() => {
    const suivants = new URLSearchParams();
    if (nonSoldees) suivants.set('non_soldees', '1');
    if (aVerifier) suivants.set('a_verifier', '1');
    if (detail) suivants.set('id', detail);
    setParams(suivants, { replace: true });
  }, [nonSoldees, aVerifier, detail, setParams]);

  function trierPar(colonne: Tri) {
    if (colonne === tri) setSens((s) => (s === 'asc' ? 'desc' : 'asc'));
    else {
      setTri(colonne);
      setSens(colonne === 'client_nom' ? 'asc' : 'desc');
    }
    setPage(1);
  }

  function reinitialiser() {
    setRecherche('');
    setFrom('');
    setTo('');
    setSociete('');
    setStatut('');
    setNonSoldees(false);
    setAVerifier(false);
    setPage(1);
  }

  const filtresActifs =
    Boolean(recherche || from || to || societe || statut) || nonSoldees || aVerifier;

  return (
    <>
      <PageHeader
        titre="Commandes"
        description="Le cahier de commandes : chaque ligne est une commande client."
        actions={
          <Button
            variant="primary"
            icone={<Plus aria-hidden className="h-4 w-4" />}
            onClick={() => setFormulaire({ ouvert: true, commande: null })}
          >
            Nouvelle commande
          </Button>
        }
      />

      <PageBody>
        <Card>
          <div className="space-y-3 px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-0 flex-1 sm:max-w-sm">
                <Search
                  aria-hidden
                  className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
                />
                <Input
                  value={recherche}
                  onChange={(e) => {
                    setRecherche(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Nom, téléphone ou N° de bon…"
                  className="pl-8"
                  aria-label="Rechercher une commande"
                />
              </div>

              <Select
                value={statut}
                onChange={(e) => {
                  setStatut(e.target.value as CommandeStatut | '');
                  setPage(1);
                }}
                className="w-auto"
                aria-label="Filtrer par statut"
              >
                <option value="">Tous les statuts</option>
                {COMMANDE_STATUTS.map((s) => (
                  <option key={s} value={s}>
                    {COMMANDE_STATUT_LABELS[s]}
                  </option>
                ))}
              </Select>

              <Select
                value={societe}
                onChange={(e) => {
                  setSociete(e.target.value);
                  setPage(1);
                }}
                className="w-auto"
                aria-label="Filtrer par société"
              >
                <option value="">Toutes les sociétés</option>
                {(societes ?? []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>

              {isFetching ? <span className="text-xs text-ink-muted">Actualisation…</span> : null}
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <label className="flex items-center gap-2 text-sm text-ink-muted">
                Du
                <Input
                  type="date"
                  value={from}
                  onChange={(e) => {
                    setFrom(e.target.value);
                    setPage(1);
                  }}
                  className="w-auto"
                />
                au
                <Input
                  type="date"
                  value={to}
                  onChange={(e) => {
                    setTo(e.target.value);
                    setPage(1);
                  }}
                  className="w-auto"
                />
              </label>

              <Checkbox
                label="Non soldées"
                checked={nonSoldees}
                onChange={(e) => {
                  setNonSoldees(e.target.checked);
                  setPage(1);
                }}
              />
              <Checkbox
                label="À vérifier"
                checked={aVerifier}
                onChange={(e) => {
                  setAVerifier(e.target.checked);
                  setPage(1);
                }}
              />

              {filtresActifs ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={reinitialiser}
                  icone={<X aria-hidden className="h-3.5 w-3.5" />}
                >
                  Réinitialiser
                </Button>
              ) : null}
            </div>
          </div>

          {isPending ? (
            <ChargementBloc />
          ) : error ? (
            <EtatErreur erreur={error} onReessayer={() => void refetch()} />
          ) : data.data.length === 0 ? (
            <EtatVide
              titre="Aucune commande"
              description={
                filtresActifs
                  ? 'Aucune commande ne correspond à ces filtres.'
                  : 'Créez une commande, ou importez votre feuille Excel existante.'
              }
              icone={<ClipboardList aria-hidden className="h-6 w-6" />}
              action={
                <Button
                  variant="primary"
                  onClick={() => setFormulaire({ ouvert: true, commande: null })}
                >
                  Nouvelle commande
                </Button>
              }
            />
          ) : (
            <>
              <Table>
                <THead>
                  <TR>
                    <THTri colonne="date_commande" triActuel={tri} sens={sens} onTrier={trierPar}>
                      Date
                    </THTri>
                    <THTri colonne="client_nom" triActuel={tri} sens={sens} onTrier={trierPar}>
                      Nom &amp; Prénom
                    </THTri>
                    <TH>Tél</TH>
                    <TH>N° de Bon</TH>
                    <TH>OD</TH>
                    <TH>OG</TH>
                    <TH>ADD</TH>
                    <TH>Type verre</TH>
                    <TH numerique>Prix</TH>
                    <TH>Monture</TH>
                    <TH numerique>Prix</TH>
                    <TH>Lentilles</TH>
                    <TH numerique>Prix</TH>
                    <THTri colonne="total" triActuel={tri} sens={sens} onTrier={trierPar} numerique>
                      Total
                    </THTri>
                    <TH numerique>Avance</TH>
                    <THTri colonne="reste" triActuel={tri} sens={sens} onTrier={trierPar}>
                      Reste
                    </THTri>
                    <TH>Société</TH>
                    <TH>Statut</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.data.map((c) => (
                    <TR key={c.id} onClick={() => setDetail(c.id)} alerte={c.a_verifier}>
                      <TD className="whitespace-nowrap text-ink-muted">{date(c.date_commande)}</TD>
                      <TD className="whitespace-nowrap font-medium">
                        <span className="flex items-center gap-1.5">
                          {tronquer(c.client_nom, 26)}
                          {c.a_verifier ? <BadgeAVerifier /> : null}
                        </span>
                      </TD>
                      <TD className="whitespace-nowrap text-ink-muted">
                        {telephone(c.client_tel)}
                      </TD>
                      <TD className="whitespace-nowrap font-mono text-xs text-ink-muted">
                        {c.numero_bon ?? '—'}
                      </TD>
                      <TD className="whitespace-nowrap font-mono text-xs">{ordonnance(c.od)}</TD>
                      <TD className="whitespace-nowrap font-mono text-xs">{ordonnance(c.og)}</TD>
                      <TD className="whitespace-nowrap font-mono text-xs">{c.addition ?? '—'}</TD>
                      <TD>{tronquer(c.type_verre, 18)}</TD>
                      <TD numerique className="text-ink-muted">
                        {c.prix_verre === null ? '—' : dh(c.prix_verre)}
                      </TD>
                      <TD>{tronquer(c.monture, 18)}</TD>
                      <TD numerique className="text-ink-muted">
                        {c.prix_monture === null ? '—' : dh(c.prix_monture)}
                      </TD>
                      <TD>{tronquer(c.lentilles, 18)}</TD>
                      <TD numerique className="text-ink-muted">
                        {c.prix_lentilles === null ? '—' : dh(c.prix_lentilles)}
                      </TD>
                      <TD numerique className="font-semibold">
                        {dh(c.total)}
                      </TD>
                      <TD numerique className="text-ink-muted">
                        {dh(c.avance)}
                      </TD>
                      <TD>
                        <BadgeReste reste={c.reste} />
                      </TD>
                      <TD className="whitespace-nowrap text-ink-muted">{c.societe ?? '—'}</TD>
                      <TD>
                        <BadgeStatutCommande statut={c.statut} />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
              <Pagination meta={data.meta} onPage={setPage} libelle="commandes" />
            </>
          )}
        </Card>
      </PageBody>

      <CommandeDrawer
        id={detail}
        onFermer={() => setDetail(null)}
        onModifier={(commande) => {
          setDetail(null);
          setFormulaire({ ouvert: true, commande });
        }}
      />

      <CommandeForm
        ouvert={formulaire.ouvert}
        commande={formulaire.commande}
        clientInitial={formulaire.client ?? null}
        onFermer={() => setFormulaire({ ouvert: false, commande: null })}
      />
    </>
  );
}
